import React from "react";
import { Card, Button, Form, Select, DatePicker, InputNumber, Radio, Spin, Tooltip } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getFieldLabel } from "app/pages/EarlyBirdOfferBuilder/fieldLibrary";
import { EarlyBirdConditionWithUiId } from "app/pages/EarlyBirdOfferBuilder/actionTypes";
import { ProductSelectMode, EarlyBirdAccommodationType, EarlyBirdAddOnType, StudyDateRangeMode } from "app/shared/model/EarlyBirdOfferDto";
import { formatProductId } from "app/pages/EarlyBirdOfferBuilder/productIdHelpers";
import type { BuilderPageLoadedData } from "app/pages/EarlyBirdOfferBuilder/actionTypes";
import { searchAgents, getAgentOfficesById } from "app/shared/api";
import type { Agent, AgentSearchResult } from "app/shared/model/Agent";

function toCourseIds(ids: any[] | undefined): string[] {
    if (!Array.isArray(ids)) return [];
    return ids.map((id: any) => (typeof id === "string" && /^Course-/i.test(id) ? id : formatProductId("Course", id)));
}
function toAccommodationIds(ids: any[] | undefined): string[] {
    if (!Array.isArray(ids)) return [];
    return ids.map((id: any) => (typeof id === "string" && /^Accommodation-/i.test(id) ? id : formatProductId("Accommodation", id)));
}
function toAddOnIds(ids: any[] | undefined): string[] {
    if (!Array.isArray(ids)) return [];
    return ids.map((id: any) => (typeof id === "string" && /^AddOn-/i.test(id) ? id : formatProductId("AddOn", id)));
}
function toTransferIds(ids: any[] | undefined): string[] {
    if (!Array.isArray(ids)) return [];
    return ids.map((id: any) => {
        if (typeof id !== "string") return formatProductId("Transfer", id);
        const stripped = id.replace(/^(Transfer-)+/i, "");
        return `Transfer-${stripped}`;
    });
}

function notNil<T>(value: T | null | undefined): value is T {
    return value != null;
}

function optional(label: string): React.ReactNode {
    return (
        <>
            {label} <span style={{ color: "#8A8F98", fontWeight: 400 }}>(Optional)</span>
        </>
    );
}

function studyDateLabels(mode: StudyDateRangeMode | undefined): { from: string; to: string } {
    switch (mode) {
        case StudyDateRangeMode.Within:
            return { from: "Start on/after", to: "Ends before/on" };
        case StudyDateRangeMode.Intersect:
            return { from: "Date From", to: "Date To" };
        default:
            return { from: "Starts before/on", to: "Ends on/after" };
    }
}

function uniqueById<T extends { id: string }>(items: T[]): T[] {
    const map = new Map<string, T>();
    for (const item of items) {
        if (item?.id) map.set(item.id, item);
    }
    return [...map.values()];
}

interface ConditionEditorSidebarProps {
    condition: EarlyBirdConditionWithUiId | null;
    ruleIndex: number;
    lane: "include" | "exclude";
    lookups: BuilderPageLoadedData;
    onSave: (condition: EarlyBirdConditionWithUiId) => void;
    onClose: () => void;
}

export const ConditionEditorSidebar: React.FC<ConditionEditorSidebarProps> = ({
      condition,
      lookups,
      onSave,
      onClose,
  }) => {
    const [form] = Form.useForm();

    const [agentSearchResults, setAgentSearchResults] = React.useState<AgentSearchResult[]>([]);
    const [agentSearchLoading, setAgentSearchLoading] = React.useState(false);
    const [officeAgents, setOfficeAgents] = React.useState<Agent[]>(lookups.agents ?? []);
    const [officeLoading, setOfficeLoading] = React.useState(false);

    const selectedAgentIds = Form.useWatch("agentIds", form) as string[] | undefined;
    const selectedStudyDateMode = Form.useWatch("studyDateMode", form) as StudyDateRangeMode | undefined;

    const loadAgentsWithOffices = React.useCallback(
        async (agentIds: string[]) => {
            if (!agentIds.length) {
                setOfficeAgents([]);
                return;
            }

            setOfficeLoading(true);
            try {
                const data = await getAgentOfficesById(agentIds, false);
                setOfficeAgents(uniqueById([...(lookups.agents ?? []), ...data]));
            } finally {
                setOfficeLoading(false);
            }
        },
        [lookups.agents]
    );

    React.useEffect(() => {
        if (!condition) {
            form.resetFields();
            return;
        }

        const values: Record<string, any> = { field: condition.field };

        if ("region" in condition) values.region = condition.region;
        if ("minAge" in condition) values.minAge = condition.minAge;
        if ("maxAge" in condition) values.maxAge = condition.maxAge;

        if ("bookingSystemId" in condition) values.bookingSystemId = (condition as any).bookingSystemId ?? undefined;
        if ("mode" in condition) values.mode = condition.mode;
        if ("courseIds" in condition) values.courseIds = toCourseIds((condition as any).courseIds);
        if ("minWeeks" in condition) values.minWeeks = condition.minWeeks;
        if ("maxWeeks" in condition) values.maxWeeks = condition.maxWeeks;
        if ("schoolIds" in condition) values.schoolIds = condition.schoolIds;
        if ("countryCodes" in condition) values.countryCodes = condition.countryCodes;

        if (condition.field === "agent") {
            const agents = Array.isArray((condition as any).agents) ? (condition as any).agents : [];
            values.agentIds = agents.map((x: any) => x.agentId);
            values.agentOfficeIds = agents.flatMap((x: any) => x.agentOfficeIds ?? []);
            void loadAgentsWithOffices(values.agentIds ?? []);
        }

        if (condition.field === "studyStartEndDates") {
            values.studyDateMode = condition.mode ?? StudyDateRangeMode.Covers;

            if (condition.studyStartEnd) {
                values.studyStartFrom = condition.studyStartEnd.from ? dayjs(condition.studyStartEnd.from) : null;
                values.studyStartTo = condition.studyStartEnd.to ? dayjs(condition.studyStartEnd.to) : null;
            }
        }

        if (condition.field === "arrivalDepartureDates") {
            const a = (condition as any).arrival;
            const d = (condition as any).departure;
            values.arrival = a ? { from: a.from ? dayjs(a.from) : null, to: a.to ? dayjs(a.to) : null } : undefined;
            values.departure = d ? { from: d.from ? dayjs(d.from) : null, to: d.to ? dayjs(d.to) : null } : undefined;
        }

        if ("accommodationIds" in condition) values.accommodationIds = toAccommodationIds((condition as any).accommodationIds);
        if ("addOnIds" in condition) values.addOnIds = toAddOnIds((condition as any).addOnIds);
        if ("kind" in condition && condition.field === "addOn") values.kind = (condition as any).kind;
        if ("transferIds" in condition) values.transferIds = toTransferIds((condition as any).transferIds);
        if ("kind" in condition && condition.field === "accommodation") values.kind = (condition as any).kind;

        form.setFieldsValue(values);
    }, [condition, form, loadAgentsWithOffices]);

    React.useEffect(() => {
        if (condition?.field !== "agent") return;
        void loadAgentsWithOffices(Array.isArray(selectedAgentIds) ? selectedAgentIds : []);
    }, [condition?.field, selectedAgentIds, loadAgentsWithOffices]);

    const handleAgentSearch = async (value: string) => {
        const term = String(value ?? "").trim();

        if (!term) {
            setAgentSearchResults([]);
            return;
        }

        setAgentSearchLoading(true);
        try {
            const result = await searchAgents(term, false, 50);
            setAgentSearchResults(result);
        } catch {
            setAgentSearchResults([]);
        } finally {
            setAgentSearchLoading(false);
        }
    };

    if (!condition) {
        return (
            <Card size="small" style={{ margin: 16 }}>
                <p style={{ color: "#666" }}>Select a condition to edit.</p>
            </Card>
        );
    }

    const title = getFieldLabel(condition.field);

    const agentOptions = uniqueById([
        ...(lookups.agents ?? []).map((a) => ({
            id: a.id,
            name: a.name,
        })),
        ...agentSearchResults.map((a) => ({
            id: a.id,
            name: a.name,
        })),
    ]).map((a) => ({
        label: a.name ?? a.id,
        value: a.id,
    }));

    const officeOptions = (officeAgents ?? [])
        .filter((agent) => (selectedAgentIds ?? []).includes(agent.id))
        .map((agent) => ({
            label: agent.name ?? agent.id,
            options: (agent.offices ?? []).map((office) => ({
                label: office.name ?? office.id,
                value: office.id,
            })),
        }));

    const handleClear = () => {
        const defaults: Record<string, any> = { field: condition.field };

        if (condition.field === "age") {
            defaults.minAge = undefined;
            defaults.maxAge = undefined;
        }

        if (condition.field === "bookingSystem") {
            defaults.bookingSystemId = undefined;
        }

        if (condition.field === "course") {
            defaults.mode = ProductSelectMode.Any;
            defaults.courseIds = [];
            defaults.minWeeks = undefined;
            defaults.maxWeeks = undefined;
        }

        if (condition.field === "accommodation") {
            defaults.mode = ProductSelectMode.Any;
            defaults.kind = undefined;
            defaults.accommodationIds = [];
            defaults.minWeeks = undefined;
            defaults.maxWeeks = undefined;
        }

        if (condition.field === "addOn") {
            defaults.mode = ProductSelectMode.Any;
            defaults.kind = undefined;
            defaults.addOnIds = [];
        }

        if (condition.field === "school") {
            defaults.schoolIds = [];
        }

        if (condition.field === "saleCountry") {
            defaults.countryCodes = [];
        }

        if (condition.field === "studyStartEndDates") {
            defaults.studyStartFrom = null;
            defaults.studyStartTo = null;
            defaults.studyDateMode = StudyDateRangeMode.Covers;
        }

        if (condition.field === "arrivalDepartureDates") {
            defaults.arrival = { from: null, to: null };
            defaults.departure = { from: null, to: null };
        }

        if (condition.field === "admissionRegion") {
            defaults.region = undefined;
        }

        if (condition.field === "agent") {
            defaults.agentIds = [];
            defaults.agentOfficeIds = [];
        }

        if (condition.field === "transfer") {
            defaults.mode = ProductSelectMode.Any;
            defaults.transferIds = [];
        }

        form.setFieldsValue(defaults);
    };

    const handleSave = () => {
        form.validateFields().then((values) => {
            const base: any = { ...condition };

            if (condition.field === "admissionRegion") {
                base.region = values.region;
            }

            if (condition.field === "agent") {
                const agentIds = Array.isArray(values.agentIds) ? values.agentIds.map(String) : [];
                const officeIds = Array.isArray(values.agentOfficeIds) ? values.agentOfficeIds.map(String) : [];

                const officeToAgentId = new Map<string, string>();
                for (const agent of officeAgents) {
                    for (const office of agent.offices ?? []) {
                        officeToAgentId.set(office.id, office.agentId || agent.id);
                    }
                }

                base.agents = agentIds.map((agentId: string) => {
                    const ownOfficeIds = officeIds.filter((officeId: string) => officeToAgentId.get(officeId) === agentId);
                    const agentObj = officeAgents.find((a) => a.id === agentId);
                    const agentOfficeNames = ownOfficeIds.reduce((acc: Record<string, string>, officeId: string) => {
                        const office = agentObj?.offices?.find((o) => o.id === officeId);
                        if (office?.name) acc[officeId] = office.name;
                        return acc;
                    }, {});

                    return {
                        agentId,
                        agentName: agentObj?.name,
                        ...(ownOfficeIds.length ? { agentOfficeIds: ownOfficeIds, agentOfficeNames } : {}),
                    };
                });
            }

            if (condition.field === "age") {
                base.minAge = values.minAge;
                base.maxAge = values.maxAge;
            }

            if (condition.field === "bookingSystem") {
                base.bookingSystemId =
                    values.bookingSystemId != null && String(values.bookingSystemId).trim()
                        ? String(values.bookingSystemId)
                        : undefined;
            }

            if (condition.field === "course") {
                base.mode = values.mode;
                base.courseIds = Array.isArray(values.courseIds) ? values.courseIds.filter((s: string) => s?.trim()) : undefined;
                base.minWeeks = values.minWeeks;
                base.maxWeeks = values.maxWeeks;
            }

            if (condition.field === "accommodation") {
                base.mode = values.mode;
                base.kind = values.kind;
                base.accommodationIds = Array.isArray(values.accommodationIds) ? values.accommodationIds.filter((s: string) => s?.trim()) : undefined;
                base.minWeeks = values.minWeeks;
                base.maxWeeks = values.maxWeeks;
            }

            if (condition.field === "addOn") {
                base.mode = values.mode;
                base.kind = values.kind;
                base.addOnIds = Array.isArray(values.addOnIds) ? values.addOnIds.filter((s: string) => s?.trim()).map(String) : undefined;
            }

            if (condition.field === "transfer") {
                base.mode = values.mode;
                base.transferIds = Array.isArray(values.transferIds) ? values.transferIds.filter((s: string) => s?.trim()).map(String) : undefined;
            }

            if (condition.field === "school") {
                base.schoolIds = values.schoolIds;
            }

            if (condition.field === "saleCountry") {
                base.countryCodes = values.countryCodes;
            }

            if (condition.field === "studyStartEndDates") {
                base.studyStartEnd = {
                    from: values.studyStartFrom ? dayjs(values.studyStartFrom).format("YYYY-MM-DD") : undefined,
                    to: values.studyStartTo ? dayjs(values.studyStartTo).format("YYYY-MM-DD") : undefined,
                };
                base.mode = values.studyDateMode ?? StudyDateRangeMode.Covers;
            }

            if (condition.field === "arrivalDepartureDates") {
                const a = values.arrival;
                const d = values.departure;
                base.arrival = a
                    ? {
                        from: a.from ? dayjs(a.from).format("YYYY-MM-DD") : undefined,
                        to: a.to ? dayjs(a.to).format("YYYY-MM-DD") : undefined,
                    }
                    : undefined;
                base.departure = d
                    ? {
                        from: d.from ? dayjs(d.from).format("YYYY-MM-DD") : undefined,
                        to: d.to ? dayjs(d.to).format("YYYY-MM-DD") : undefined,
                    }
                    : undefined;
            }

            onSave(base as EarlyBirdConditionWithUiId);
        });
    };

    return (
        <Card
            title={
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{title}</span>
                    <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} aria-label="Close" />
                </div>
            }
            size="small"
            style={{ margin: 16 }}
        >
            <Form form={form} layout="vertical" onFinish={handleSave}>
                {condition.field === "admissionRegion" && (
                    <Form.Item name="region" label="Admission Region" rules={[{ required: true, message: "Select Admission Region" }]}>
                        <Select
                            placeholder="Select Admission Region"
                            options={(lookups.admissionRegions ?? []).map((region) => ({
                                label: region,
                                value: region,
                            }))}
                            showSearch={{ optionFilterProp: 'label' }}
                        />
                    </Form.Item>
                )}

                {condition.field === "agent" && (
                    <>
                        <Form.Item
                            name="agentIds"
                            label="Agent"
                            rules={[{ required: true, type: "array", min: 1, message: "Select at least one agent" }]}
                        >
                            <Select
                                mode="multiple"
                                placeholder="Search Agent"
                                options={agentOptions}
                                showSearch={{
                                  filterOption: false,
                                  onSearch: handleAgentSearch,
                                  optionFilterProp: 'label',
                                }}
                                notFoundContent={agentSearchLoading ? <Spin size="small" /> : null}
                            />
                        </Form.Item>

                        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.agentIds !== curr.agentIds}>
                            {() =>
                                (form.getFieldValue("agentIds") as string[] | undefined)?.length ? (
                                    <Form.Item name="agentOfficeIds" label="Agent Offices (optional)">
                                        <Select
                                            mode="multiple"
                                            placeholder="Select Agent Office"
                                            options={officeOptions}
                                            loading={officeLoading}
                                            showSearch={{ optionFilterProp: 'label' }}
                                        />
                                    </Form.Item>
                                ) : null
                            }
                        </Form.Item>
                    </>
                )}

                {condition.field === "age" && (
                    <>
                        <Form.Item name="minAge" label={optional("Age on Arrival Min")}>
                            <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item
                            name="maxAge"
                            label={optional("Age on Arrival Max")}
                            dependencies={["minAge"]}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const min = getFieldValue("minAge");
                                        if (value != null && min != null && value < min) {
                                            return Promise.reject(new Error("Max must be ≥ min"));
                                        }
                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                        >
                            <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                    </>
                )}

                {condition.field === "bookingSystem" && (
                    <Form.Item name="bookingSystemId" label="Booking System" rules={[{ required: true }]}>
                        <Select
                            placeholder="Select Booking System"
                            options={lookups.bookingSystems.map((b) => ({ label: b.name ?? b.id, value: b.id }))}
                            showSearch={{ optionFilterProp: 'label' }}
                        />
                    </Form.Item>
                )}

                {condition.field === "course" && (
                    <>
                        <Form.Item name="mode" label="Mode" rules={[{ required: true }]}>
                            <Radio.Group
                                options={[
                                    { label: "ANY", value: ProductSelectMode.Any },
                                    { label: "SELECT", value: ProductSelectMode.Select },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.mode !== curr.mode}>
                            {() =>
                                form.getFieldValue("mode") === ProductSelectMode.Select && (
                                    <Form.Item name="courseIds" label="Courses">
                                        <Select
                                            mode="multiple"
                                            placeholder="Search Course Name"
                                            options={lookups.courses.map((c) => ({
                                                label: c.name ?? String(c.id),
                                                value: formatProductId("Course", c.id),
                                            }))}
                                            showSearch={{ optionFilterProp: 'label' }}
                                        />
                                    </Form.Item>
                                )
                            }
                        </Form.Item>
                        <Form.Item name="minWeeks" label="Number of weeks Min">
                            <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item name="maxWeeks" label="Number of weeks Max">
                            <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                    </>
                )}

                {condition.field === "school" && (
                    <Form.Item name="schoolIds" label="School" rules={[{ required: true, message: "Select at least one" }]}>
                        <Select
                            mode="multiple"
                            placeholder="Select School"
                            options={lookups.schools.map((s) => ({
                                label: s.name ?? s.code ?? String(s.id),
                                value: String(s.id),
                            }))}
                            showSearch={{ optionFilterProp: 'label' }}
                        />
                    </Form.Item>
                )}

                {condition.field === "saleCountry" && (
                    <Form.Item name="countryCodes" label="Country" rules={[{ required: true, message: "Select at least one" }]}>
                        <Select
                            mode="multiple"
                            placeholder="Country"
                            options={lookups.countries.map((c) => ({ label: c.name ?? c.id, value: c.id }))}
                            showSearch={{ optionFilterProp: 'label' }}
                        />
                    </Form.Item>
                )}

                {condition.field === "studyStartEndDates" && (
                    <>
                        <Form.Item name="studyDateMode" label="Course">
                            <Radio.Group>
                                <Tooltip
                                    title="Courses that run for the entire selected period."
                                >
                                    <Radio value={StudyDateRangeMode.Covers}>Runs throughout the date range</Radio>
                                </Tooltip>
                                <Tooltip
                                    title="Courses that start and finish inside the selected period."
                                >
                                    <Radio value={StudyDateRangeMode.Within}>Falls within the date range</Radio>
                                </Tooltip>
                                <Tooltip
                                    title="Courses that overlap the selected period, even partly."
                                >
                                    <Radio value={StudyDateRangeMode.Intersect}>Overlaps the date range</Radio>
                                </Tooltip>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item
                            name="studyStartFrom"
                            label={optional(studyDateLabels(selectedStudyDateMode).from)}
                        >
                            <DatePicker style={{ width: "100%" }} format={["DD-MMM-YYYY", "DD-MM-YYYY", "DDMMYYYY"]} />
                        </Form.Item>
                        <Form.Item
                            name="studyStartTo"
                            label={optional(studyDateLabels(selectedStudyDateMode).to)}
                            dependencies={["studyStartFrom"]}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const from = getFieldValue("studyStartFrom");
                                        if (value && from && dayjs(value).isBefore(dayjs(from), "day")) {
                                            return Promise.reject(new Error("The second date must be on or after the first"));
                                        }
                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                        >
                            <DatePicker style={{ width: "100%" }} format={["DD-MMM-YYYY", "DD-MM-YYYY", "DDMMYYYY"]} />
                        </Form.Item>
                    </>
                )}

                {condition.field === "accommodation" && (
                    <>
                        <Form.Item name="mode" label="Mode">
                            <Radio.Group
                                options={[
                                    { label: "ANY", value: ProductSelectMode.Any },
                                    { label: "TYPE", value: ProductSelectMode.Type },
                                    { label: "SELECT", value: ProductSelectMode.Select },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.mode !== curr.mode}>
                            {() =>
                                form.getFieldValue("mode") === ProductSelectMode.Type && (
                                    <Form.Item name="kind" label="Accommodation type" rules={[{ required: true, message: "Select type" }]}>
                                        <Select
                                            placeholder="Select type"
                                            options={lookups.accommodationTypes
                                                .filter(
                                                    (t) =>
                                                        t.id === EarlyBirdAccommodationType.Homestay ||
                                                        t.id === EarlyBirdAccommodationType.Residence
                                                )
                                                .map((t) => ({ label: t.name, value: t.id }))}
                                        />
                                    </Form.Item>
                                )
                            }
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.mode !== curr.mode}>
                            {() =>
                                form.getFieldValue("mode") === ProductSelectMode.Select && (
                                    <Form.Item name="accommodationIds" label="Accommodation">
                                        <Select
                                            mode="multiple"
                                            placeholder="Select Accommodation"
                                            options={lookups.accommodationRooms.filter(notNil).map((a) => ({
                                                label: a.name ?? String(a.id),
                                                value: formatProductId("Accommodation", a.id),
                                            }))}
                                            showSearch={{ optionFilterProp: 'label' }}
                                        />
                                    </Form.Item>
                                )
                            }
                        </Form.Item>
                        <Form.Item name="minWeeks" label="Number of weeks Min">
                            <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item name="maxWeeks" label="Number of weeks Max">
                            <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                    </>
                )}

                {condition.field === "addOn" && (
                    <>
                        <Form.Item name="mode" label="Mode">
                            <Radio.Group
                                options={[
                                    { label: "ANY", value: ProductSelectMode.Any },
                                    { label: "TYPE", value: ProductSelectMode.Type },
                                    { label: "SELECT", value: ProductSelectMode.Select },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.mode !== curr.mode}>
                            {() =>
                                form.getFieldValue("mode") === ProductSelectMode.Type && (
                                    <Form.Item name="kind" label="AddOn type" rules={[{ required: true, message: "Select type" }]}>
                                        <Select
                                            placeholder="Select type"
                                            options={lookups.addOnTypes
                                                .filter((t) => t.id >= 1 && t.id <= 4)
                                                .map((t) => ({ label: t.name, value: t.id as EarlyBirdAddOnType }))}
                                        />
                                    </Form.Item>
                                )
                            }
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.mode !== curr.mode}>
                            {() =>
                                form.getFieldValue("mode") === ProductSelectMode.Select && (
                                    <Form.Item name="addOnIds" label="AddOns">
                                        <Select
                                            mode="multiple"
                                            placeholder="Select Classes"
                                            options={lookups.addOns.map((a) => ({
                                                label: a.name ?? String(a.id),
                                                value: formatProductId("AddOn", a.id),
                                            }))}
                                            showSearch={{ optionFilterProp: 'label' }}
                                        />
                                    </Form.Item>
                                )
                            }
                        </Form.Item>
                    </>
                )}

                {condition.field === "transfer" && (
                    <>
                        <Form.Item name="mode" label="Mode" rules={[{ required: true }]}>
                            <Radio.Group
                                options={[
                                    { label: "ANY", value: ProductSelectMode.Any },
                                    { label: "SELECT", value: ProductSelectMode.Select },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.mode !== curr.mode}>
                            {() =>
                                form.getFieldValue("mode") === ProductSelectMode.Select && (
                                    <Form.Item name="transferIds" label="Transfers">
                                        <Select
                                            mode="multiple"
                                            placeholder="Select Transfer"
                                            options={lookups.transfers.map((t) => ({
                                                label: t.name ?? String(t.id),
                                                value: formatProductId("Transfer", t.id),
                                            }))}
                                            showSearch={{ optionFilterProp: 'label' }}
                                        />
                                    </Form.Item>
                                )
                            }
                        </Form.Item>
                    </>
                )}

                {condition.field === "arrivalDepartureDates" && (
                    <>
                        <div style={{ marginBottom: 12, fontSize: 12, color: "#666" }}>
                            Matches the booking&apos;s overall <strong>arrival</strong> and{" "}
                            <strong>departure</strong> dates - the earliest start and latest end across all
                            course &amp; accommodation lines, not a single product. Both <em>From</em> and{" "}
                            <em>To</em> are inclusive; leave a field empty to keep that side open.
                            <br />
                            <strong>Departure From - Departure To</strong> is the checkout date: for most courses this is the day
                            after the last course day (a course ending 01-Oct departs 02-Oct), while
                            accommodation departs on its end date.
                        </div>
                        <Form.Item name={["arrival", "from"]} label={optional("Arrival From")}>
                            <DatePicker style={{ width: "100%" }} format={["DD-MMM-YYYY", "DD-MM-YYYY", "DDMMYYYY"]} />
                        </Form.Item>
                        <Form.Item
                            name={["arrival", "to"]}
                            label={optional("Arrival To")}
                            dependencies={[["arrival", "from"]]}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const from = getFieldValue(["arrival", "from"]);
                                        if (value && from && dayjs(value).isBefore(dayjs(from), "day")) {
                                            return Promise.reject(new Error("Arrival To must be ≥ Arrival From"));
                                        }
                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                        >
                            <DatePicker style={{ width: "100%" }} format={["DD-MMM-YYYY", "DD-MM-YYYY", "DDMMYYYY"]} />
                        </Form.Item>
                        <Form.Item name={["departure", "from"]} label={optional("Departure From")}>
                            <DatePicker style={{ width: "100%" }} format={["DD-MMM-YYYY", "DD-MM-YYYY", "DDMMYYYY"]} />
                        </Form.Item>
                        <Form.Item
                            name={["departure", "to"]}
                            label={optional("Departure To")}
                            dependencies={[["departure", "from"]]}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const from = getFieldValue(["departure", "from"]);
                                        if (value && from && dayjs(value).isBefore(dayjs(from), "day")) {
                                            return Promise.reject(new Error("Departure To must be ≥ Departure From"));
                                        }
                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                        >
                            <DatePicker style={{ width: "100%" }} format={["DD-MMM-YYYY", "DD-MM-YYYY", "DDMMYYYY"]} />
                        </Form.Item>
                    </>
                )}

                <Form.Item>
                    <Button type="default" onClick={handleClear} style={{ marginRight: 8 }}>
                        Clear
                    </Button>
                    <Button type="primary" htmlType="submit">
                        Apply
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};